import { useMemo, useState } from "react";
import { useImmer } from "use-immer";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../../UI/ModalDialog";
import FileDropInput from "../../UI/FileDropInput";
import Button from "../../UI/Button";
import Card from "../../UI/Card";
import Callout from "../../UI/Callout";
import Text from "../../UI/Text";
import Status from "../../UI/Status";
import Tooltip from "../../UI/Tooltip";
import { Table } from "../../UI/Table";
import IconButton from "../../UI/IconButton";
import { IconFile, IconX } from "@tabler/icons-react";
// @ts-expect-error pure js
import { formatFileSize } from "../../../api/Functions";
// @ts-expect-error pure js
import { currencyFormatter, dateConvert } from "../../../api/Functions";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../../UI/SelectStandalone";
// @ts-expect-error pure js
import { handleAPIError } from "../../../utils";
// @ts-expect-error pure js
import useTenants from "../../../hooks/use-tenants";
// @ts-expect-error pure js
import useProperties from "../../../hooks/use-properties";
import useCafStatementImport from "../../../hooks/use-caf-statement-import";
import type {
  CafCommitLine,
  CafMatchSource,
  CafPreviewLine,
  CafPreviewResponse,
} from "../../../models/cafImport";
import type { Tenant } from "../../../models/tenant";
import type { Property } from "../../../models/property";
import { Selectable } from "../../UI/Selectable";
import styles from "./CafStatementImportDialog.module.css";

interface TenantOption {
  value: number;
  label: string;
}

type Step = "upload" | "preview";

interface CafStatementImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: () => void;
}

const ACCEPT = ".pdf";

export default function CafStatementImportDialog({
  open,
  onOpenChange,
  onImported,
}: CafStatementImportDialogProps) {
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useImmer<CafPreviewLine[]>([]);
  const [meta, setMeta] = useState<Omit<CafPreviewResponse, "lines"> | null>(
    null,
  );
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    number | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [importSummary, setImportSummary] = useState<string>("");

  const { preview, commit } = useCafStatementImport();
  const { tenants } = useTenants() as { tenants: Tenant[] };
  const { propertiesById } = useProperties({ withById: true }) as {
    propertiesById: Record<number, Property>;
  };

  const tenantOptions: TenantOption[] = useMemo(
    () =>
      (tenants ?? []).map((tenant) => {
        const property = propertiesById?.[tenant.id_bien];
        return {
          value: tenant.id,
          label: property ? `${tenant.nom} (${property.nom})` : tenant.nom,
        };
      }),
    [tenants, propertiesById],
  );

  function resetAll() {
    setStep("upload");
    setFile(null);
    setRows([]);
    setMeta(null);
    setSelectedTransactionId(null);
    setError("");
    setImportSummary("");
  }

  async function handlePreview() {
    if (!file) {
      setError("Sélectionnez le bordereau PDF reçu de la CAF");
      return;
    }
    setError("");
    setIsLoading(true);
    try {
      const result = await preview(file);
      setRows(result.lines);
      setMeta(result);
      setSelectedTransactionId(result.matched_transactions[0]?.id ?? null);
      setStep("preview");
    } catch (e) {
      setError(handleAPIError(e));
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCommit(checkedIds: (string | number)[]) {
    if (!file || !meta) return;
    setError("");
    const ids = new Set(checkedIds);
    const lines: CafCommitLine[] = rows
      .map((row, index) => ({ row, index }))
      .filter(({ row, index }) => ids.has(`line-${index}`) && row.id_tenant)
      .map(({ row }) => ({
        id_tenant: row.id_tenant as number,
        montant: row.montant,
        label: `${row.nom} — ${row.numero_allocataire}`,
        numero_allocataire: row.numero_allocataire,
      }));

    if (lines.length === 0) {
      setError("Aucune ligne sélectionnée avec un locataire affecté");
      return;
    }

    setIsLoading(true);
    try {
      const result = await commit(file, {
        lines,
        date_virement: meta.date_virement,
        id_transaction: selectedTransactionId,
      });
      setImportSummary(
        result.id_transaction
          ? `${result.created_balances} paiement(s) CAF enregistré(s) et ventilé(s) sur la transaction bancaire rapprochée.`
          : `${result.created_balances} paiement(s) CAF enregistré(s) (aucune transaction bancaire correspondante, paiements créés manuellement).`,
      );
      onImported();
    } catch (e) {
      setError(handleAPIError(e));
    } finally {
      setIsLoading(false);
    }
  }

  const selectableItems = useMemo(
    () => rows.map((_, index) => ({ id: `line-${index}` })),
    [rows],
  );

  const transactionOptions = useMemo(
    () =>
      (meta?.matched_transactions ?? []).map((transaction) => ({
        value: transaction.id,
        label: `${dateConvert(transaction.date_transac)} — ${
          transaction.description || "Virement"
        } — ${currencyFormatter(transaction.montant)}`,
      })),
    [meta],
  );

  const affectedCount = rows.filter((row) => row.id_tenant).length;
  const unaffectedCount = rows.length - affectedCount;
  const totalMismatch =
    meta?.total != null &&
    Math.abs(meta.total - (meta.total_lines ?? 0)) > 0.01;

  return (
    <ModalDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) resetAll();
        onOpenChange(nextOpen);
      }}
      size="xl"
      header="Importer un relevé CAF"
      subtitle="Les paiements détectés sont enregistrés en « Paiement CAF » pour chaque locataire."
    >
      {importSummary ? (
        <>
          <ModalDialogBody>
            <Text>{importSummary}</Text>
          </ModalDialogBody>
          <ModalDialogFooter
            start={
              <Button variant="secondary" onClick={() => resetAll()}>
                Importer un autre relevé
              </Button>
            }
          >
            <Button onClick={() => onOpenChange(false)} variant="primary">
              Fermer
            </Button>
          </ModalDialogFooter>
        </>
      ) : step === "upload" ? (
        <>
          <ModalDialogBody>
            <Callout variant="info">
              Importez le document PDF reçu de la CAF ou de la MSA, pas un scan
              ni une photo : le fichier doit contenir du texte sélectionnable.
            </Callout>
            <div className={styles.section}>
              <FileDropInput
                accept={ACCEPT}
                text="Déposez le bordereau CAF ou cliquez pour parcourir"
                format="PDF uniquement"
                onFiles={(files: File[]) => setFile(files[0] ?? null)}
              />
              {file && (
                <Card padding="xs" className={styles.fileRow}>
                  <div className={styles.docThumb}>
                    <IconFile size={28} stroke={1.5} />
                  </div>
                  <div className={styles.fileInfo}>
                    <Text weight="bold">{file.name}</Text>
                    <Text variant="deci">{formatFileSize(file.size)}</Text>
                  </div>
                  <IconButton
                    icon={IconX}
                    label="Supprimer"
                    onClick={() => setFile(null)}
                  />
                </Card>
              )}
            </div>
            <Callout variant="danger">{error}</Callout>
          </ModalDialogBody>
          <ModalDialogFooter
            start={
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
            }
          >
            <Button
              onClick={handlePreview}
              variant="primary"
              disabled={!file || isLoading}
            >
              {isLoading ? "Analyse en cours…" : "Analyser le bordereau"}
            </Button>
          </ModalDialogFooter>
        </>
      ) : (
        <Selectable items={selectableItems} defaultSelectAll rangeSelect>
          <ModalDialogBody className={styles.previewBody}>
            <div className={styles.summaryGrid}>
              <SummaryItem label="Lignes détectées" value={rows.length} />
              <SummaryItem
                label="Locataire affecté"
                value={affectedCount}
                color="success"
              />
              <SummaryItem
                label="À affecter"
                value={unaffectedCount}
                color={unaffectedCount > 0 ? "warning" : "muted"}
              />
              <div className={styles.summaryItem}>
                <Text color="muted">Montant du virement</Text>
                <Text variant="mega" weight="bold">
                  {currencyFormatter(meta?.total ?? meta?.total_lines ?? 0)}
                </Text>
              </div>
            </div>

            {meta?.already_imported && (
              <Callout variant="warning" title="Bordereau déjà importé">
                Ce bordereau a déjà été importé : le valider à nouveau créerait
                des paiements en double.
              </Callout>
            )}

            {totalMismatch && (
              <Callout variant="warning">
                Le total des lignes détectées (
                {currencyFormatter(meta?.total_lines ?? 0)}) diffère du montant
                du virement indiqué sur le bordereau (
                {currencyFormatter(meta?.total ?? 0)}). Vérifiez les lignes
                avant de valider.
              </Callout>
            )}

            {meta && meta.matched_transactions.length > 0 ? (
              <div className={styles.matchSection}>
                <div>
                  <Text weight="medium">Transaction bancaire à ventiler</Text>
                  <Text>
                    Transactions non affectées au même montant, datées du mois
                    du bordereau, du mois précédent ou du mois suivant. Les
                    lignes validées seront ventilées sur la transaction choisie.
                  </Text>
                </div>
                <ReactSelectStandalone
                  options={transactionOptions}
                  isClearable
                  placeholder="Aucune — créer des paiements CAF manuels"
                  value={
                    transactionOptions.find(
                      (opt: { value: number }) =>
                        opt.value === selectedTransactionId,
                    ) ?? null
                  }
                  onChange={(opt: { value: number } | null) =>
                    setSelectedTransactionId(opt?.value ?? null)
                  }
                />
              </div>
            ) : (
              <Callout variant="neutral">
                Aucune transaction bancaire correspondante n'a été trouvée : les
                paiements CAF seront enregistrés directement sur chaque
                locataire, sans transaction associée.
              </Callout>
            )}

            <Table>
              <Table.Header>
                <Table.Column>
                  <Selectable.Checkbox selectAll />
                </Table.Column>
                <Table.Column>N° allocataire</Table.Column>
                <Table.Column>Nom détecté</Table.Column>
                <Table.Column>Locataire</Table.Column>
                <Table.Column align="right">Montant</Table.Column>
              </Table.Header>
              <Table.Body>
                {rows.map((row, index) => (
                  <Table.Row key={`line-${index}`}>
                    <Table.Cell>
                      <Selectable.Checkbox
                        value={`line-${index}`}
                        index={index}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Text variant="deci">{row.numero_allocataire}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <div className={styles.nomCell}>
                        <Text as="span" variant="deci" title={row.nom}>
                          {row.nom}
                        </Text>
                        <div>
                          <SourceBadge source={row.match_source} />
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell className={styles.selectCell}>
                      <ReactSelectStandalone
                        options={tenantOptions}
                        isClearable
                        value={
                          tenantOptions.find(
                            (opt) => opt.value === row.id_tenant,
                          ) ?? null
                        }
                        onChange={(opt: TenantOption | null) =>
                          setRows((draft) => {
                            draft[index].id_tenant = opt?.value ?? null;
                            draft[index].match_source = opt ? "manual" : "none";
                          })
                        }
                      />
                    </Table.Cell>
                    <Table.Cell align="right">
                      <Text
                        as="span"
                        variant="deci"
                        weight="medium"
                        color="success"
                      >
                        {currencyFormatter(row.montant)}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </ModalDialogBody>

          <Callout variant="danger">{error}</Callout>
          <ModalDialogFooter
            start={
              <Button variant="secondary" onClick={() => setStep("upload")}>
                Retour
              </Button>
            }
          >
            <Selectable.Action
              render={(checkedIds) => {
                const ids = new Set(checkedIds);
                const selectedWithTenant = rows.filter(
                  (row, index) => ids.has(`line-${index}`) && row.id_tenant,
                ).length;
                return (
                  <Button
                    onClick={() => handleCommit(checkedIds)}
                    variant="primary"
                    disabled={
                      isLoading ||
                      selectedWithTenant === 0 ||
                      meta?.already_imported
                    }
                  >
                    {isLoading
                      ? "Import en cours…"
                      : `Valider ${selectedWithTenant} paiement(s) CAF`}
                  </Button>
                );
              }}
            />
          </ModalDialogFooter>
        </Selectable>
      )}
    </ModalDialog>
  );
}

function SummaryItem({
  label,
  value,
  color = "neutral",
}: {
  label: string;
  value: number;
  color?: "neutral" | "success" | "warning" | "muted";
}) {
  return (
    <div className={styles.summaryItem}>
      <Text color="muted">{label}</Text>
      <Text variant="mega" weight="bold" color={color}>
        {value}
      </Text>
    </div>
  );
}

function SourceBadge({ source }: { source: CafMatchSource }) {
  if (source === "numero_allocataire") {
    return (
      <Tooltip content="Locataire détecté grâce au numéro allocataire renseigné sur sa fiche — le rapprochement le plus fiable.">
        <Status as="span" color="success" text="N° allocataire" />
      </Tooltip>
    );
  }
  if (source === "nom") {
    return (
      <Tooltip content="Locataire détecté par son nom, retrouvé dans le nom du bénéficiaire indiqué sur le bordereau. Vérifiez l'affectation avant de valider.">
        <Status as="span" color="success" text="Nom" />
      </Tooltip>
    );
  }
  if (source === "manual") {
    return (
      <Tooltip content="Locataire choisi manuellement dans la liste.">
        <Status as="span" color="neutral" text="Manuel" />
      </Tooltip>
    );
  }
  return (
    <Text as="span" variant="deci">
      Aucune suggestion
    </Text>
  );
}
